// «ارسال اعلان آزمایشی»: push a notification to this browser and find out how
// far it got.
//
// «اعلان نمی‌آید» has three very different causes, and only one is ours:
//  - it never reached the browser (network, the push service, a stale
//    subscription),
//  - it reached the browser and the OS hid it (notifications off for Chrome in
//    macOS/Windows settings, Focus / Do Not Disturb) — the common one, and
//    invisible from the server, where every push looks delivered,
//  - it showed and the person simply did not see it.
// The service worker reports back when it has shown the test (see
// `reportSelfTest` in service-worker.ts); this module waits for that.

import { sendWebPushTest } from 'services/webPush'
import {
    SELF_TEST_TAG_PREFIX,
    WEB_PUSH_SHOWN,
    WebPushShownMessage,
} from 'swPush'
import { currentBrowserSubscription, enableWebPush } from './index'

// FCM usually lands in a couple of seconds; past this the page gives its
// «نرسید» advice, and still updates if the push turns up afterwards.
export const SELF_TEST_WAIT_MS = 30_000
const LATE_WAIT_MS = 3 * 60_000

export type SelfTestFailure =
    | 'not_subscribed' // this browser has no subscription we could repair
    | 'too_soon' // one test every few seconds
    | 'capped' // announce's daily limit on test pushes
    | 'unavailable' // the backend or announce is down
    | 'offline' // the request itself did not go through

export type SelfTestOutcome =
    | { kind: 'shown'; listed: boolean | null; ms: number }
    | { kind: 'show_failed'; error: string; ms: number }
    | {
          kind: 'not_received'
          ms: number
          // Settles if the push arrives after all.
          late: Promise<WebPushShownMessage | null>
      }
    | { kind: 'send_failed'; reason: SelfTestFailure; repaired: boolean }

// Same shape as an axios 409, so both paths to «not subscribed» meet in `failureOf`.
const NOT_SUBSCRIBED = { response: { status: 409 } }

const makeNonce = (): string => {
    const bytes = new Uint8Array(6)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/** The service worker's report for `tag`, or null after `ms` or on `cancel`. */
const waitForShown = (tag: string, ms: number) => {
    let finish: (message: WebPushShownMessage | null) => void = () => {}
    const report = new Promise<WebPushShownMessage | null>((resolve) => {
        const onMessage = (event: MessageEvent) => {
            const data = event.data as WebPushShownMessage | undefined
            if (data?.type === WEB_PUSH_SHOWN && data.tag === tag) finish(data)
        }
        const timer = window.setTimeout(() => finish(null), ms)
        finish = (message) => {
            window.clearTimeout(timer)
            navigator.serviceWorker.removeEventListener('message', onMessage)
            resolve(message)
        }
        navigator.serviceWorker.addEventListener('message', onMessage)
    })
    return { report, cancel: () => finish(null) }
}

const failureOf = (error: unknown): SelfTestFailure => {
    const response = (error as {
        response?: { status?: number; data?: { detail?: string } }
    })?.response
    if (!response) return 'offline'
    if (response.status === 409) return 'not_subscribed'
    if (response.status === 429)
        return response.data?.detail === 'capped' ? 'capped' : 'too_soon'
    return 'unavailable'
}

export const runWebPushSelfTest = async (): Promise<SelfTestOutcome> => {
    const nonce = makeNonce()
    const tag = `${SELF_TEST_TAG_PREFIX}${nonce}`
    let repaired = false

    const send = async () => {
        const subscription = await currentBrowserSubscription()
        if (!subscription) throw NOT_SUBSCRIBED
        return sendWebPushTest({ endpoint: subscription.endpoint, nonce })
    }

    const started = Date.now()
    // Listen before asking: the push can beat the HTTP response back.
    const shown = waitForShown(tag, SELF_TEST_WAIT_MS)
    try {
        try {
            await send()
        } catch (error) {
            if (failureOf(error) !== 'not_subscribed') throw error
            // The browser lost its subscription, or the server retired it.
            // Permission is still granted, so this re-registers without a dialog.
            repaired = true
            if ((await enableWebPush('self_test')) !== 'subscribed') throw error
            await send()
        }
    } catch (error) {
        shown.cancel()
        return { kind: 'send_failed', reason: failureOf(error), repaired }
    }

    const report = await shown.report
    const ms = Date.now() - started
    if (!report) {
        return { kind: 'not_received', ms, late: waitForShown(tag, LATE_WAIT_MS).report }
    }
    return report.error
        ? { kind: 'show_failed', error: report.error, ms }
        : { kind: 'shown', listed: report.listed, ms }
}
